import type {
  CompositeAmbientSoundSlotRef,
  CompositeDefRef,
  CompositeFlameSlotRef,
  CompositeFlickerConfigRef,
  CompositeLightSlotRef,
  CompositeMeshSlotRef,
  SceneCompositeRef,
} from '../scene/SceneManifest';
import type {
  AmbientSoundSlotDef,
  CompositeDef,
  CompositeState,
  FlameSlotDef,
  FlickerConfigDef,
  LightEmitterSlotsDef,
  LightSlotDef,
  MeshSlotDef,
  SceneCompositeInstance,
} from './types';

/**
 * Translate the manifest-shape composite def (loose `number[]` arrays, JSON-
 * friendly nullables) into the engine-shape `CompositeDef` (`readonly` tuples,
 * typed enums). The shapes are intentionally structurally close — this exists
 * to centralise the type assertions that would otherwise scatter across every
 * load path (SceneManifestLoader, TiledObjectStreamer, and the editor
 * registry's API converter).
 *
 * v1 only supports the `lightEmitter` template; the discriminator is checked
 * at the entry, so adding a second template requires updating both this
 * function and the engine `CompositeDef.template` union.
 */
export function manifestDefToEngineDef(ref: CompositeDefRef): CompositeDef {
  if (ref.template !== 'lightEmitter') {
    throw new Error(`Unknown composite template '${ref.template}' (id='${ref.id}')`);
  }
  return {
    id: ref.id,
    name: ref.name,
    template: 'lightEmitter',
    slots: {
      mesh: meshSlot(ref.slots.mesh),
      light: lightSlot(ref.slots.light),
      flicker: flickerConfig(ref.slots.flicker),
      flames: ref.slots.flames?.map(flameSlot),
      ambientSound: ref.slots.ambientSound ? ambientSoundSlot(ref.slots.ambientSound) : undefined,
    } satisfies LightEmitterSlotsDef,
  };
}

/** Translate a manifest placement into the engine-shape `SceneCompositeInstance`. */
export function manifestPlacementToEngine(
  placement: SceneCompositeRef,
): SceneCompositeInstance {
  return {
    instanceId: placement.instanceId,
    compositeId: placement.compositeId,
    position: [placement.position[0], placement.position[1], placement.position[2]],
    rotation: [
      placement.rotation[0],
      placement.rotation[1],
      placement.rotation[2],
      placement.rotation[3],
    ],
    scale: placement.scale,
    state: placement.state as Partial<CompositeState> | undefined,
    seed: placement.seed,
  };
}

function meshSlot(slot: CompositeMeshSlotRef): MeshSlotDef {
  return {
    modelId: slot.modelId,
    meshName: slot.meshName,
    localPosition: slot.localPosition
      ? [slot.localPosition[0], slot.localPosition[1], slot.localPosition[2]]
      : undefined,
    localRotation: slot.localRotation
      ? [
          slot.localRotation[0],
          slot.localRotation[1],
          slot.localRotation[2],
          slot.localRotation[3],
        ]
      : undefined,
    localScale: slot.localScale,
  };
}

function lightSlot(slot: CompositeLightSlotRef): LightSlotDef {
  return {
    type: slot.type,
    color: [slot.color[0], slot.color[1], slot.color[2]],
    intensity: slot.intensity,
    innerRange: slot.innerRange,
    outerRange: slot.outerRange,
    innerConeAngle: slot.innerConeAngle,
    outerConeAngle: slot.outerConeAngle,
    shadowMode: slot.shadowMode,
    localPosition: slot.localPosition
      ? [slot.localPosition[0], slot.localPosition[1], slot.localPosition[2]]
      : undefined,
    localRotation: slot.localRotation
      ? [
          slot.localRotation[0],
          slot.localRotation[1],
          slot.localRotation[2],
          slot.localRotation[3],
        ]
      : undefined,
    anchorSocket: slot.anchorSocket,
  };
}

function flickerConfig(c: CompositeFlickerConfigRef): FlickerConfigDef {
  return {
    amplitude: c.amplitude,
    frequencyHz: c.frequencyHz,
    profile: c.profile,
    noiseSeed: c.noiseSeed,
  };
}

function ambientSoundSlot(slot: CompositeAmbientSoundSlotRef): AmbientSoundSlotDef {
  return {
    clipId: slot.clipId,
    volume: slot.volume,
    localPosition: slot.localPosition
      ? [slot.localPosition[0], slot.localPosition[1], slot.localPosition[2]]
      : undefined,
  };
}

function flameSlot(slot: CompositeFlameSlotRef): FlameSlotDef {
  // Tuple-narrow optional color/position arrays so the engine type stays
  // strict while the manifest type stays JSON-friendly (number[]). Same
  // pattern as meshSlot/lightSlot above.
  const triple = (a: number[] | undefined): readonly [number, number, number] | undefined =>
    a ? [a[0], a[1], a[2]] : undefined;
  return {
    localPosition: triple(slot.localPosition),
    anchorSocket: slot.anchorSocket,
    width: slot.width,
    height: slot.height,
    intensity: slot.intensity,
    baseColor: triple(slot.baseColor),
    midColor: triple(slot.midColor),
    tipColor: triple(slot.tipColor),
    scrollSpeed: slot.scrollSpeed,
    taper: slot.taper,
    noiseScale: slot.noiseScale,
    softness: slot.softness,
  };
}
